import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { UserAuth } from '../AuthContext';

export type TDTier = 'none' | 'trial' | 'starter' | 'pro' | 'pctds';

export interface TierAccess {
  tier: TDTier;
  isActive: boolean;
  trialDaysLeft: number;
  canCreateEvents: boolean;
  canUseClock: boolean;
  canUsePosterGenerator: boolean;
  canUseSeatCards: boolean;
  canUseGuestPass: boolean;
  canUseRoomPage: boolean;
  canUseRecurring: boolean;
  canFindDealers: boolean;
  canUsePublicEvents: boolean;
  canUseCharitySplit: boolean;
  canUsePCTDS: boolean;
  loading: boolean;
}

const TIER_ORDER: Record<TDTier, number> = {
  none: 0, trial: 3, starter: 1, pro: 2, pctds: 4
};

const atLeast = (tier: TDTier, required: TDTier): boolean =>
  TIER_ORDER[tier] >= TIER_ORDER[required];

export const useTDTier = (): TierAccess => {
  const { user } = UserAuth();
  const [tier, setTier] = useState<TDTier>('none');
  const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) { setLoading(false); return; }
    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) {
        const data = snap.data();
        const t = (data.td_tier ?? (data.is_td ? 'trial' : 'none')) as TDTier;
        setTier(t);
        setTrialExpiresAt(data.trial_expires_at ?? null);
      }
      setLoading(false);
    });
    return unsub;
  }, [user?.uid]);

  // Check if trial is still valid
  const trialValid = trialExpiresAt
    ? new Date(trialExpiresAt) > new Date()
    : false;

  const trialDaysLeft = trialExpiresAt
    ? Math.max(0, Math.ceil((new Date(trialExpiresAt).getTime() - Date.now()) / 86400000))
    : 0;

  // Effective tier — if trial is active, treat as pro
  const effectiveTier: TDTier = tier === 'trial'
    ? (trialValid ? 'pro' : 'none')
    : tier;

  return {
    tier,
    isActive: effectiveTier !== 'none',
    trialDaysLeft,
    canCreateEvents:      atLeast(effectiveTier, 'starter'),
    canUseClock:          atLeast(effectiveTier, 'starter'),
    canUsePosterGenerator: atLeast(effectiveTier, 'pro'),
    canUseSeatCards:      atLeast(effectiveTier, 'pro'),
    canUseGuestPass:      atLeast(effectiveTier, 'pro'),
    canUseRoomPage:       atLeast(effectiveTier, 'pro'),
    canUseRecurring:      atLeast(effectiveTier, 'pro'),
    canFindDealers:       atLeast(effectiveTier, 'pro'),
    canUsePublicEvents:   atLeast(effectiveTier, 'pro'),
    canUseCharitySplit:   atLeast(effectiveTier, 'pro'),
    canUsePCTDS:          atLeast(effectiveTier, 'pctds'),
    loading,
  };
};
